/* eslint-disable max-lines-per-function */
import { Abteilungsleiter } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abteilungsleiter';
import { ArbeitsbereichOption } from '@/domain/mitarbeiter/features/rotationsplan/entities/ArbeitsbereichOption';
import { Standort as RotationsplanStandort } from '@/domain/mitarbeiter/features/rotationsplan/entities/Standort';
import { useCreateVorlageStation } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useCreateVorlageStation/useCreateVorlageStation';
import type { RotationsplanVorlageDetails } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanVorlageById/useRotationsplanVorlageById';
import { useMitarbeiterAppForm } from '@/domain/mitarbeiter/shared/components/form/hooks';
import { Standort } from '@/features/standortContext/entities/standort';
import {
  Mitarbeiter_RotationsplanVorlageDauerEinheit,
  Mitarbeiter_RotationsplanVorlageErinnerungsZeit,
  Mitarbeiter_RotationsplanVorlageStationTyp,
} from '@/gql/graphql';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import z from 'zod';

export const LERNZIELE_MAX_LENGTH = 400;

export function useForm({
  vorlage,
  vorlageAbschnittId,
  currentStandort,
}: {
  vorlage: RotationsplanVorlageDetails;
  vorlageAbschnittId: string;
  currentStandort: Standort;
}) {
  const [createVorlageStation] = useCreateVorlageStation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const schema = z
    .object({
      standort: z.instanceof(RotationsplanStandort, {
        message: t('generalValidationError.required'),
      }),
      arbeitsbereich: z.instanceof(ArbeitsbereichOption, {
        message: t('generalValidationError.required'),
      }),
      dauer: z
        .number()
        .int()
        .min(1, {
          message: t(
            'domain.mitarbeiter.rotationsplan.vorlagen.createStation.fields.dauer.min',
          ),
        }),
      dauerEinheit: z.enum(Mitarbeiter_RotationsplanVorlageDauerEinheit),
      lernziele: z.string().max(LERNZIELE_MAX_LENGTH, {
        message: t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.lernziele.tooLong',
        ),
      }),
      abteilungsleiter: z.instanceof(Abteilungsleiter).optional(),
      fristErinnerung: z.boolean(),
      erinnerungszeiten: z
        .enum(Mitarbeiter_RotationsplanVorlageErinnerungsZeit)
        .array(),
    })
    .superRefine((data, ctx) => {
      if (data.fristErinnerung && data.erinnerungszeiten.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: t(
            'domain.mitarbeiter.fristen.erinnerungsZeit.validationError',
          ),
          path: ['erinnerungszeiten'],
        });
      }
    });

  type FormValues = z.infer<typeof schema>;

  async function handleSubmit(value: FormValues) {
    const showError = () =>
      toast({
        variant: 'destructive',
        title: t(
          'domain.mitarbeiter.rotationsplan.vorlagen.createStation.error',
        ),
      });

    try {
      const result = await createVorlageStation({
        input: {
          vorlageId: vorlage.id,
          vorlageAbschnittId,
          typ: Mitarbeiter_RotationsplanVorlageStationTyp.Arbeitsbereich,
          standortId: value.standort.id,
          arbeitsbereichId: value.arbeitsbereich.id,
          abteilungsleiterId: value.abteilungsleiter?.id,
          dauer: value.dauer,
          dauerEinheit: value.dauerEinheit,
          lernziele: value.lernziele.trim(),
          erinnerungszeiten: value.fristErinnerung
            ? value.erinnerungszeiten
            : [],
        },
      });

      if (!result.data?.mitarbeiter_createVorlageStation.success) {
        showError();
        return;
      }

      toast({
        title: t(
          'domain.mitarbeiter.rotationsplan.vorlagen.createStation.success',
        ),
      });
      navigate(`/mitarbeiter/rotationsplaene/vorlagen/${vorlage.id}`, {
        replace: true,
      });
    } catch (error) {
      Sentry.captureException(error);
      showError();
    }
  }

  return useMitarbeiterAppForm({
    defaultValues: {
      // die Vorlage gilt nur für Standorte im selben Land
      standort:
        vorlage.land === currentStandort.land
          ? new RotationsplanStandort({
              id: currentStandort.uid,
              name: currentStandort.name,
              storenummer: currentStandort.storeNummer,
            })
          : undefined,
      arbeitsbereich: undefined,
      dauer: 1,
      dauerEinheit: Mitarbeiter_RotationsplanVorlageDauerEinheit.Monate,
      lernziele: '',
      abteilungsleiter: undefined,
      fristErinnerung: false,
      erinnerungszeiten: [],
    } as unknown as FormValues,
    validators: {
      onSubmit: schema,
    },
    onSubmit: async ({ value }) => await handleSubmit(value),
  });
}
